import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  HostListener,
  inject,
  model,
  ModelSignal,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { ActiveTimerBarComponent } from '@app/components/active-timer-bar/active-timer-bar.component';
import { LangToggleComponent } from '@app/shared/components/lang-toggle/lang-toggle.component';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { AuthService } from '@app/shared/services/auth.service';
import { CompaniesDataService } from '@app/shared/services/companies-data.service';
import { DesktopSidebarPreferencesService } from '@app/shared/services/desktop-sidebar-preferences.service';
import { ProjectsDataService } from '@app/shared/services/projects-data.service';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { LoaderService } from '@app/shared/services/shared/loader.service';
import { UrlSegment } from '@app/shared/types/enums/url-segment.enum';
import { Company } from '@app/shared/types/models/company/company.model';
import { Project } from '@app/shared/types/models/project/project.model';
import { MenuItem } from '@app/shared/types/models/shared/menu-item';
import { User } from '@app/shared/types/models/user/user.model';
import { UiSelectComponent } from '@app/shared/ui/select/ui-select.component';
import { UiSpinnerDirective } from '@app/shared/ui/spinner/ui-spinner.directive';
import { ProffeoUiSelectOption } from '@app/shared/ui/types/select.types';
import { TranslatePipe } from '@ngx-translate/core';

/** Delay before collapsing the desktop sidebar hover peek after mouse leave. */
const DESKTOP_SIDEBAR_HOVER_AUTOCLOSE_MS = 500;

@Component({
  selector: 'proffeo-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  imports: [
    MatIconModule,
    NgClass,
    NgTemplateOutlet,
    CdkScrollable,
    RouterModule,
    UiSelectComponent,
    TranslatePipe,
    MatMenuModule,
    ActiveTimerBarComponent,
    MatTooltip,
    LangToggleComponent,
    UiSpinnerDirective
  ]
})
export class LayoutComponent implements OnInit, OnDestroy {
  private readonly authService: AuthService = inject(AuthService);
  private readonly router: Router = inject(Router);
  private readonly companyService: CompaniesDataService = inject(CompaniesDataService);
  private readonly deviceService: DeviceService = inject(DeviceService);
  private readonly projectService: ProjectsDataService = inject(ProjectsDataService);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);
  private readonly activeCompanyService: ActiveCompanyService = inject(ActiveCompanyService);
  private readonly loaderService: LoaderService = inject(LoaderService);
  private readonly desktopSidebarPreferences: DesktopSidebarPreferencesService = inject(
    DesktopSidebarPreferencesService
  );
  private menuAutocloseTimeout: ReturnType<typeof setTimeout> | null = null;

  protected readonly loading = this.loaderService.loading;
  protected readonly isAuthenticated: Signal<boolean> = this.authService.isAuthenticated;

  protected readonly companyOptions = computed((): ProffeoUiSelectOption<Company>[] =>
    (this.companies() ?? []).map(company => ({
      value: company,
      label: company.name ?? ''
    }))
  );

  protected readonly projectOptions = computed((): ProffeoUiSelectOption<Project>[] =>
    (this.projects() ?? []).map(project => ({
      value: project,
      label: project.name ?? ''
    }))
  );

  /** Desktop: pinned open pushes layout. Mobile: drawer open. */
  protected pinned: WritableSignal<boolean> = signal(!this.deviceService.isMobile());
  /** Desktop-only temporary overlay while hovering a collapsed rail. */
  protected hoverPeek: WritableSignal<boolean> = signal(false);
  protected visuallyExpanded: Signal<boolean> = computed(() => this.pinned() || this.hoverPeek());
  protected currentUser: Signal<User> = this.authService.currentUser;
  protected companies: Signal<Company[] | undefined> = computed(() =>
    this.currentUser() === null || this.currentUser() === undefined
      ? undefined
      : this.companyService.getUserCompanies(this.currentUser()?.id)()
  );
  protected projects: Signal<Project[] | undefined> = computed(() =>
    this.activeCompany() === null || this.activeCompany() === undefined
      ? undefined
      : this.projectService.getCompanyProjects(this.activeCompany()?.id)()
  );
  protected activeCompany: Signal<Company | undefined> = this.activeCompanyService.activeCompany;
  protected activeProject: Signal<Project | undefined> = this.activeProjectService.activeProject;
  protected isMobile: Signal<boolean> = this.deviceService.isMobile;

  protected dashboardMenuItems: Signal<MenuItem | undefined> = computed(
    () => this.getDashboardMenuItems() ?? undefined
  );
  protected activeProjectMenuItems: Signal<MenuItem | undefined> = computed(() =>
    this.activeProject()?.id ? this.getProjectMenuItems(this.activeProject()!) : undefined
  );
  protected activeCompanyMenuItems: Signal<MenuItem | undefined> = computed(() =>
    this.activeCompany()?.id ? this.getCompanyMenuItems(this.activeCompany()!) : undefined
  );

  public timerVisible: ModelSignal<boolean> = model(false);

  @HostListener('window:keydown.m', ['$event']) public onKeyDownEvent = (event: KeyboardEvent): void => {
    const target = event.target as HTMLElement;
    const isInputField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

    if (!isInputField) {
      this.toggleMenu();
    }
  };

  public ngOnInit(): void {
    if (this.isMobile()) {
      this.pinned.set(false);
      return;
    }

    const savedExpanded = this.desktopSidebarPreferences.expanded();
    if (savedExpanded !== null) {
      this.pinned.set(savedExpanded);
    }
  }

  protected openChangePasswordDialog(): void {
    this.dialogService.openChangePasswordDialog();
  }

  protected handleNavigationClick(): void {
    if (this.isMobile()) {
      this.pinned.set(false);
    }
  }

  protected compareCompanies = (c1: Company, c2: Company): boolean => {
    return c1 && c2 ? c1.id === c2.id : c1 === c2;
  };

  protected compareProjects = (p1: Project, p2: Project): boolean => {
    return p1 && p2 ? p1.id === p2.id : p1 === p2;
  };

  protected onCompanySelected(company: Company | Company[] | null): void {
    if (!company || Array.isArray(company)) {
      return;
    }

    if (this.isMobile()) {
      this.pinned.set(false);
    }
    this.activeCompanyService.setActiveCompany(company);
  }

  protected onProjectSelected(project: Project | Project[] | null): void {
    if (!project || Array.isArray(project)) {
      return;
    }

    if (this.isMobile()) {
      this.pinned.set(false);
    }
    void this.activeProjectService.setActiveProject(project);
  }

  protected isMenuItemActive(link: string): boolean {
    return this.router.isActive(link, {
      paths: 'exact',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored'
    });
  }

  protected getDashboardMenuItems(): MenuItem {
    return new MenuItem({
      id: 'dashboard',
      type: 'group',
      icon: 'dashboard',
      link: '/',
      titleTranslationKey: 'menu-section-app',
      exactMatch: true,
      children: [
        new MenuItem({
          id: 'dashboard',
          type: 'basic',
          link: `/home`,
          titleTranslationKey: 'menu-item-title-dashboard',
          icon: 'dashboard',
          exactMatch: true,
          disabled: true,
          disabledTooltipTranslationKey: 'menu-item-coming-soon-info'
        }),
        new MenuItem({
          id: 'reports',
          type: 'basic',
          icon: 'table_view',
          link: `/${UrlSegment.REPORTS}/${UrlSegment.WORK_TIME}`,
          titleTranslationKey: 'menu-item-title-reports',
          subtitleTranslationKey: 'menu-item-subtitle-system-reports',
          exactMatch: true
        }),
        new MenuItem({
          id: 'security',
          type: 'basic',
          icon: 'lock_open',
          link: `${UrlSegment.SECURITY}/${UrlSegment.USERS}`,
          titleTranslationKey: 'menu-item-title-user-privileges',
          subtitleTranslationKey: 'menu-item-subtitle-user-privileges',
          exactMatch: true,
          disabled: true,
          disabledTooltipTranslationKey: 'menu-item-coming-soon-info'
        })
      ]
    });
  }

  protected getProjectMenuItems(project?: Project): MenuItem | undefined {
    if (!project) {
      return undefined;
    }

    return new MenuItem({
      id: 'project',
      type: 'group',
      titleTranslationKey: 'menu-section-project',
      exactMatch: true,
      children: [
        new MenuItem({
          id: 'tasks-list',
          type: 'basic',
          icon: 'task',
          link: `${UrlSegment.COMPANIES}/${this.activeCompany()?.id}/${UrlSegment.PROJECTS}/${project.id}/${UrlSegment.TASKS}`,
          titleTranslationKey: 'menu-item-title-tasks',
          subtitleTranslationKey: 'menu-item-subtitle-project-task-list',
          exactMatch: true
        }),
        new MenuItem({
          id: 'project-industries',
          type: 'basic',
          icon: 'business_center',
          link: `${UrlSegment.COMPANIES}/${this.activeCompany()?.id}/${UrlSegment.PROJECTS}/${project.id}/${UrlSegment.INDUSTRIES}`,
          titleTranslationKey: 'menu-item-title-industries',
          subtitleTranslationKey: 'menu-item-subtitle-project-industries',
          exactMatch: true
        }),
        new MenuItem({
          id: 'locations',
          type: 'basic',
          icon: 'location_on',
          link: `${UrlSegment.COMPANIES}/${this.activeCompany()?.id}/${UrlSegment.PROJECTS}/${project.id}/${UrlSegment.LOCATIONS}`,
          titleTranslationKey: 'menu-item-title-locations',
          subtitleTranslationKey: 'menu-item-subtitle-project-locations',
          exactMatch: true
        }),
        new MenuItem({
          id: 'participants',
          type: 'basic',
          icon: 'groups',
          link: `${UrlSegment.COMPANIES}/${this.activeCompany()?.id}/${UrlSegment.PROJECTS}/${project.id}/${UrlSegment.PARTICIPANTS}`,
          titleTranslationKey: 'menu-item-title-participants',
          subtitleTranslationKey: 'menu-item-subtitle-project-groups-and-users',
          exactMatch: true
        }),
        new MenuItem({
          id: 'tags',
          type: 'basic',
          icon: 'style',
          link: `${UrlSegment.COMPANIES}/${this.activeCompany()?.id}/${UrlSegment.PROJECTS}/${project.id}/${UrlSegment.TAGS}`,
          titleTranslationKey: 'project-tags.menu-item-title',
          exactMatch: true
        }),
        new MenuItem({
          id: 'osh',
          type: 'basic',
          icon: 'note_alt',
          link: `${UrlSegment.COMPANIES}/${this.activeCompany()?.id}/${UrlSegment.PROJECTS}/${project.id}/${UrlSegment.OSH}`,
          titleTranslationKey: 'menu-item-title-osh',
          exactMatch: true
        })
      ]
    });
  }

  protected getCompanyMenuItems(company?: Company): MenuItem | undefined {
    if (!company) {
      return undefined;
    }

    const companyMenuItems = [
      new MenuItem({
        id: 'projects',
        type: 'basic',
        link: `${UrlSegment.COMPANIES}/${company.id}/${UrlSegment.PROJECTS_LIST}/${UrlSegment.ACTIVE}`,
        titleTranslationKey: 'menu-item-title-projects',
        subtitleTranslationKey: 'menu-item-subtitle-available-project',
        icon: 'task_alt',
        exactMatch: true
      })
    ];

    if (company.directUserCompany) {
      companyMenuItems.push(
        new MenuItem({
          id: 'industries',
          type: 'basic',
          icon: 'business_center',
          link: `${UrlSegment.COMPANIES}/${company.id}/${UrlSegment.INDUSTRIES}`,
          titleTranslationKey: 'menu-item-title-industries',
          subtitleTranslationKey: 'menu-item-subtitle-industries-list',
          exactMatch: true
        })
      );

      companyMenuItems.push(
        new MenuItem({
          id: 'users',
          type: 'basic',
          icon: 'person_search',
          link: `${UrlSegment.COMPANIES}/${company.id}/${UrlSegment.USERS}`,
          titleTranslationKey: 'menu-item-title-users',
          subtitleTranslationKey: 'menu-item-subtitle-system-accounts',
          exactMatch: true
        })
      );

      companyMenuItems.push(
        new MenuItem({
          id: 'groups',
          type: 'basic',
          icon: 'group',
          link: `${UrlSegment.COMPANIES}/${company.id}/${UrlSegment.GROUPS}`,
          titleTranslationKey: 'menu-item-title-groups',
          subtitleTranslationKey: 'menu-item-subtitle-system-user-groups',
          exactMatch: true
        })
      );

      companyMenuItems.push(
        new MenuItem({
          id: 'cooperations',
          type: 'basic',
          icon: 'handshake',
          link: `${UrlSegment.COMPANIES}/${company.id}/${UrlSegment.COOPERATIONS}`,
          titleTranslationKey: 'menu-item-title-cooperations',
          subtitleTranslationKey: 'menu-item-subtitle-cooperations',
          exactMatch: true
        })
      );

      companyMenuItems.push(
        new MenuItem({
          id: 'osh',
          type: 'basic',
          icon: 'note_alt',
          link: `${UrlSegment.COMPANIES}/${company.id}/${UrlSegment.OSH}`,
          titleTranslationKey: 'menu-item-title-osh',
          exactMatch: true
        })
      );
    }

    return new MenuItem({
      id: 'company',
      type: 'group',
      titleTranslationKey: 'menu-section-company',
      exactMatch: true,
      children: companyMenuItems
    });
  }

  protected toggleMenu(): void {
    const nextPinned = !this.pinned();
    this.pinned.set(nextPinned);
    this.hoverPeek.set(false);

    if (!this.isMobile()) {
      this.clearMenuAutocloseTimeout();
      this.desktopSidebarPreferences.setExpanded(nextPinned);
    }
  }

  public ngOnDestroy(): void {
    this.clearMenuAutocloseTimeout();
  }

  protected turnOffMenuAutoclose(): void {
    // Hover peek only when the rail is not pinned open.
    if (this.pinned() || this.isMobile()) {
      return;
    }

    this.clearMenuAutocloseTimeout();
    this.hoverPeek.set(true);
  }

  protected triggerMenuAutoclose(event?: MouseEvent): void {
    if (this.pinned() || this.isMobile() || !this.hoverPeek()) {
      return;
    }

    // Ignore leave events that stay inside the menu (e.g. DOM rebuild on expand).
    if (event) {
      const relatedTarget = event.relatedTarget;
      const currentTarget = event.currentTarget;
      if (relatedTarget instanceof Node && currentTarget instanceof Node && currentTarget.contains(relatedTarget)) {
        return;
      }
    }

    this.clearMenuAutocloseTimeout();
    this.menuAutocloseTimeout = setTimeout(() => {
      this.menuAutocloseTimeout = null;
      if (!this.pinned()) {
        this.hoverPeek.set(false);
      }
    }, DESKTOP_SIDEBAR_HOVER_AUTOCLOSE_MS);
  }

  private clearMenuAutocloseTimeout(): void {
    if (this.menuAutocloseTimeout !== null) {
      clearTimeout(this.menuAutocloseTimeout);
      this.menuAutocloseTimeout = null;
    }
  }
}
